import Image from 'next/image';
import Link from 'next/link';

import styles from '@/components/agents/agents.module.css';

import clsxm from '@/lib/clsxm';

import {
  ArrowGlyph,
  buttonPrimary,
  delay,
  SectionHeading,
  tone,
} from '@/components/agents/primitives';
import { useReveal } from '@/components/agents/useReveal';

import { WORKSHOP_PARTNERS } from '@/constants/workshops';
import { WORKSHOP_CONTACT_ID } from '@/utils/scrollToSection';

import { PHOTOS, useAboutTranslation } from './kit';

export default function AboutWorkshops() {
  const { t } = useAboutTranslation();
  const { ref, state } = useReveal<HTMLDivElement>(0.2);

  return (
    <section id='workshops' className='scroll-mt-8 py-20 sm:py-28'>
      <div className='mx-auto grid max-w-7xl gap-14 px-6 lg:grid-cols-12 lg:items-center lg:gap-16 lg:px-8'>
        <div
          ref={ref}
          data-reveal={state}
          className={clsxm(
            styles.reveal,
            'grid grid-cols-6 gap-3 sm:gap-4 lg:col-span-7',
          )}
        >
          <figure
            className={clsxm(
              styles.rv,
              'relative col-span-6 aspect-[16/10] overflow-hidden rounded-[28px] bg-[#e8e8f0] dark:bg-white/5',
            )}
          >
            <Image
              src={PHOTOS.workshopJune}
              alt={t('Business owners after an Easy-Peasy.AI workshop')}
              fill
              unoptimized
              className='object-cover object-[50%_60%]'
            />
          </figure>
          <figure
            className={clsxm(
              styles.rv,
              'relative col-span-3 aspect-[4/3] overflow-hidden rounded-[24px] bg-[#e8e8f0] dark:bg-white/5',
            )}
            style={delay(120)}
          >
            <Image
              src={PHOTOS.mariannaTeaching}
              alt={t('Marianna Olefyrenko teaching an AI workshop')}
              fill
              unoptimized
              className='object-cover object-[50%_25%]'
            />
          </figure>
          <figure
            className={clsxm(
              styles.rv,
              'relative col-span-3 aspect-[4/3] overflow-hidden rounded-[24px] bg-[#e8e8f0] dark:bg-white/5',
            )}
            style={delay(240)}
          >
            <Image
              src={PHOTOS.capitaLand}
              alt={t('A generative AI workshop with the CapitaLand team')}
              fill
              unoptimized
              className='object-cover'
            />
          </figure>
        </div>

        <div className='lg:col-span-5'>
          <SectionHeading
            size='md'
            title={t('We teach AI in person, too.')}
            tail={t('Hands-on workshops in Singapore.')}
            lead={t(
              'We run practical AI workshops for business owners and company teams. Everyone leaves knowing how to use AI in their own work.',
            )}
          />

          <div className='mt-8'>
            <p className={clsxm('text-[13px] font-medium', tone.quiet)}>
              {t('We’ve run sessions with')}
            </p>
            <ul
              className={clsxm(
                'mt-3 flex flex-wrap gap-x-4 gap-y-1.5 text-[15px] font-semibold tracking-[-0.01em]',
                tone.ink,
              )}
            >
              {WORKSHOP_PARTNERS.map((partner) => (
                <li key={partner}>{partner}</li>
              ))}
            </ul>
          </div>

          <div className='mt-9 flex flex-wrap items-center gap-x-6 gap-y-4'>
            <Link
              href={`/ai-workshops#${WORKSHOP_CONTACT_ID}`}
              className={buttonPrimary}
            >
              {t('Plan a workshop')}
              <ArrowGlyph />
            </Link>
            <Link
              href='/ai-workshops'
              className={clsxm(
                'inline-flex items-center gap-1.5 text-[15px] font-semibold underline-offset-4 hover:underline',
                tone.ink,
              )}
            >
              {t('See our workshops')}
              <ArrowGlyph className='h-3.5 w-3.5' />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
